import React, { useEffect, useState, useRef, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getForagesCartographie, getPartenaires } from '../services/api';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const PARTNER_COLORS: Record<string, string> = {
  'TAKKOU LIGGUEY': '#e74c3c',
  'KARANG EAU': '#2196F3',
  'ENTREPRISE PATHE DIALLO': '#4CAF50',
  'COLOBANE EAU': '#FF9800',
  'GIE BOKK GUISS GUISS': '#9C27B0',
  'T3E Karantaba': '#009688',
  'G.I.E PASSY EAU ET ASSAINISSEMENT': '#795548',
  'GIE SUXALI NDIAYE NDIAYE': '#E91E63',
  'GIE DJIBOUNG': '#3F51B5',
};

function getColor(partenaire: string): string {
  return PARTNER_COLORS[partenaire] || '#607D8B';
}

function createIcon(color: string, size: number = 28) {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background: ${color}; width: ${size}px; height: ${size}px; border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg); border: 3px solid #fff;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3); position: relative;
    "><div style="
      width: ${size * 0.36}px; height: ${size * 0.36}px; background: #fff; border-radius: 50%;
      position: absolute; top: ${size * 0.21}px; left: ${size * 0.21}px; transform: rotate(45deg);
    "></div></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
}

function markerSize(nbAbonnes: number): number {
  if (nbAbonnes >= 2000) return 38;
  if (nbAbonnes >= 1000) return 34;
  if (nbAbonnes >= 500) return 30;
  if (nbAbonnes >= 100) return 26;
  return 22;
}

type ViewMode = 'legend' | 'list' | 'detail';

const Cartographie: React.FC = () => {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);

  const [forages, setForages] = useState<any[]>([]);
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [filtrePartenaire, setFiltrePartenaire] = useState('');
  const [filtreRecherche, setFiltreRecherche] = useState('');
  const [filtreLocalite, setFiltreLocalite] = useState('');
  const [filtreProfondeur, setFiltreProfondeur] = useState('');
  const [filtreAbonnes, setFiltreAbonnes] = useState('');
  const [selectedForage, setSelectedForage] = useState<any>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('legend');
  const [loading, setLoading] = useState(true);
  const [mapStyle, setMapStyle] = useState<'standard' | 'satellite'>('standard');

  useEffect(() => {
    getPartenaires().then(r => setPartenaires(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (filtrePartenaire) params.partenaire_id = filtrePartenaire;
    getForagesCartographie(params).then(r => {
      setForages(r.data);
      setLoading(false);
    });
  }, [filtrePartenaire]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = L.map(mapContainerRef.current, {
      center: [13.5, -15.8],
      zoom: 8,
      zoomControl: true,
    });

    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    });
    osmLayer.addTo(map);
    (map as any)._osmLayer = osmLayer;

    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Toggle map style
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current as any;
    if (map._currentTile) map.removeLayer(map._currentTile);

    let tile: L.TileLayer;
    if (mapStyle === 'satellite') {
      tile = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '&copy; Esri',
        maxZoom: 18,
      });
    } else {
      tile = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 18,
      });
    }
    // Remove old layers
    map.eachLayer((l: any) => { if (l instanceof L.TileLayer) map.removeLayer(l); });
    tile.addTo(map);
  }, [mapStyle]);

  // Filtered forages
  const filtered = useMemo(() => {
    return forages.filter(f => {
      if (filtreRecherche) {
        const q = filtreRecherche.toLowerCase();
        const match = f.libelle?.toLowerCase().includes(q) ||
          f.localite_nom?.toLowerCase().includes(q) ||
          f.reference?.toLowerCase().includes(q) ||
          f.partenaire?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filtreLocalite && f.localite_nom !== filtreLocalite) return false;
      if (filtreProfondeur) {
        const p = Number(f.profondeur) || 0;
        if (filtreProfondeur === '0-50' && p > 50) return false;
        if (filtreProfondeur === '50-100' && (p < 50 || p > 100)) return false;
        if (filtreProfondeur === '100+' && p < 100) return false;
      }
      if (filtreAbonnes) {
        const a = Number(f.nbAbonnes) || 0;
        if (filtreAbonnes === '0' && a > 0) return false;
        if (filtreAbonnes === '1-100' && (a < 1 || a > 100)) return false;
        if (filtreAbonnes === '100-500' && (a < 100 || a > 500)) return false;
        if (filtreAbonnes === '500+' && a < 500) return false;
      }
      return true;
    });
  }, [forages, filtreRecherche, filtreLocalite, filtreProfondeur, filtreAbonnes]);

  // Localites list
  const localites = useMemo(() => {
    const set = new Set<string>();
    forages.forEach(f => { if (f.localite_nom) set.add(f.localite_nom); });
    return Array.from(set).sort();
  }, [forages]);

  // Update markers
  useEffect(() => {
    if (!mapRef.current || !markersRef.current) return;
    markersRef.current.clearLayers();
    const bounds: L.LatLngExpression[] = [];

    filtered.forEach(f => {
      const lat = parseFloat(f.lat);
      const lng = parseFloat(f.lng);
      if (isNaN(lat) || isNaN(lng) || lat === 0) return;

      bounds.push([lat, lng]);
      const color = getColor(f.partenaire);
      const size = markerSize(f.nbAbonnes || 0);
      const marker = L.marker([lat, lng], { icon: createIcon(color, size) });

      marker.bindPopup(`
        <div style="min-width:220px; font-family: 'Segoe UI', sans-serif;">
          <div style="font-weight:700; font-size:15px; color:${color}; margin-bottom:4px;">${f.libelle}</div>
          <div style="font-size:11px; color:#999; margin-bottom:8px;">${f.reference || ''} &middot; ${f.partenaire}</div>
          <table style="font-size:13px; width:100%;">
            <tr><td style="color:#888; padding:2px 8px 2px 0;">Localite</td><td>${f.localite_nom || '-'}</td></tr>
            <tr><td style="color:#888; padding:2px 8px 2px 0;">Profondeur</td><td>${f.profondeur ? f.profondeur + ' m' : '-'}</td></tr>
            <tr><td style="color:#888; padding:2px 8px 2px 0;">Debit</td><td>${f.debit ? f.debit + ' m3/h' : '-'}</td></tr>
            <tr><td style="color:#888; padding:2px 8px 2px 0;">Abonnes</td><td style="font-weight:700; color:#2196F3;">${(f.nbAbonnes || 0).toLocaleString('fr-FR')}</td></tr>
            <tr><td style="color:#888; padding:2px 8px 2px 0;">Secteurs</td><td>${f.nbSecteurs || 0}</td></tr>
          </table>
        </div>
      `);

      marker.on('click', () => {
        setSelectedForage(f);
        setViewMode('detail');
      });
      marker.addTo(markersRef.current!);
    });

    if (bounds.length > 0) {
      mapRef.current.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [50, 50], maxZoom: 12 });
    }
  }, [filtered]);

  // Stats
  const stats = useMemo(() => {
    const totalAbonnes = filtered.reduce((s, f) => s + (f.nbAbonnes || 0), 0);
    const totalSecteurs = filtered.reduce((s, f) => s + (f.nbSecteurs || 0), 0);
    const avgProfondeur = filtered.length > 0
      ? Math.round(filtered.reduce((s, f) => s + (Number(f.profondeur) || 0), 0) / filtered.filter(f => f.profondeur > 0).length || 0)
      : 0;
    const avgDebit = filtered.length > 0
      ? Math.round(filtered.reduce((s, f) => s + (Number(f.debit) || 0), 0) / filtered.filter(f => f.debit > 0).length * 10 || 0) / 10
      : 0;
    const partenairesCounts: Record<string, { count: number; abonnes: number }> = {};
    filtered.forEach(f => {
      if (!partenairesCounts[f.partenaire]) partenairesCounts[f.partenaire] = { count: 0, abonnes: 0 };
      partenairesCounts[f.partenaire].count++;
      partenairesCounts[f.partenaire].abonnes += f.nbAbonnes || 0;
    });
    return { totalAbonnes, totalSecteurs, avgProfondeur, avgDebit, partenairesCounts };
  }, [filtered]);

  const resetFilters = () => {
    setFiltrePartenaire('');
    setFiltreRecherche('');
    setFiltreLocalite('');
    setFiltreProfondeur('');
    setFiltreAbonnes('');
  };

  const hasFilters = filtrePartenaire || filtreRecherche || filtreLocalite || filtreProfondeur || filtreAbonnes;

  const focusForage = (f: any) => {
    setSelectedForage(f);
    setViewMode('detail');
    if (mapRef.current) {
      mapRef.current.setView([parseFloat(f.lat), parseFloat(f.lng)], 13);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 16, color: '#333' }}>Cartographie des forages</h2>

      {/* KPI Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 14 }}>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #2196F3', padding: '12px 16px' }}>
          <div className="kpi-v2-label" style={{ fontSize: 11 }}>Forages</div>
          <div className="kpi-v2-value" style={{ fontSize: 24, color: '#2196F3' }}>{filtered.length}</div>
        </div>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #FF9800', padding: '12px 16px' }}>
          <div className="kpi-v2-label" style={{ fontSize: 11 }}>Abonnes</div>
          <div className="kpi-v2-value" style={{ fontSize: 24, color: '#FF9800' }}>{stats.totalAbonnes.toLocaleString('fr-FR')}</div>
        </div>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #4CAF50', padding: '12px 16px' }}>
          <div className="kpi-v2-label" style={{ fontSize: 11 }}>Secteurs</div>
          <div className="kpi-v2-value" style={{ fontSize: 24, color: '#4CAF50' }}>{stats.totalSecteurs}</div>
        </div>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #9C27B0', padding: '12px 16px' }}>
          <div className="kpi-v2-label" style={{ fontSize: 11 }}>Prof. moyenne</div>
          <div className="kpi-v2-value" style={{ fontSize: 24, color: '#9C27B0' }}>{stats.avgProfondeur} <span style={{ fontSize: 13 }}>m</span></div>
        </div>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #009688', padding: '12px 16px' }}>
          <div className="kpi-v2-label" style={{ fontSize: 11 }}>Debit moyen</div>
          <div className="kpi-v2-value" style={{ fontSize: 24, color: '#009688' }}>{stats.avgDebit} <span style={{ fontSize: 13 }}>m3/h</span></div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: 14, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
          <select value={filtrePartenaire} onChange={e => setFiltrePartenaire(e.target.value)} className="filter-select" style={{ fontSize: 13 }}>
            <option value="">Tous les partenaires</option>
            {partenaires.map((p: any) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
          </select>

          <select value={filtreLocalite} onChange={e => setFiltreLocalite(e.target.value)} className="filter-select" style={{ fontSize: 13 }}>
            <option value="">Toutes les localites</option>
            {localites.map(l => <option key={l} value={l}>{l}</option>)}
          </select>

          <select value={filtreProfondeur} onChange={e => setFiltreProfondeur(e.target.value)} className="filter-select" style={{ fontSize: 13 }}>
            <option value="">Profondeur</option>
            <option value="0-50">0 - 50 m</option>
            <option value="50-100">50 - 100 m</option>
            <option value="100+">100+ m</option>
          </select>

          <select value={filtreAbonnes} onChange={e => setFiltreAbonnes(e.target.value)} className="filter-select" style={{ fontSize: 13 }}>
            <option value="">Nb abonnes</option>
            <option value="0">Aucun abonne</option>
            <option value="1-100">1 - 100</option>
            <option value="100-500">100 - 500</option>
            <option value="500+">500+</option>
          </select>

          <input
            type="text"
            placeholder="&#128269; Rechercher..."
            value={filtreRecherche}
            onChange={e => setFiltreRecherche(e.target.value)}
            className="filter-input"
            style={{ minWidth: 180, fontSize: 13 }}
          />

          {hasFilters && (
            <button onClick={resetFilters} style={{
              background: '#f44336', color: '#fff', border: 'none', padding: '6px 14px',
              borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600
            }}>Reinitialiser</button>
          )}

          <span style={{ color: '#888', fontSize: 12, marginLeft: 'auto' }}>
            {loading ? 'Chargement...' : `${filtered.length} / ${forages.length} forages`}
          </span>
        </div>
      </div>

      {/* Map + Side Panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 330px', gap: 14, height: 'calc(100vh - 330px)', minHeight: 450 }}>
        {/* Map */}
        <div style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'relative' }}>
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
          {/* Map controls overlay */}
          <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, display: 'flex', gap: 4 }}>
            <button
              onClick={() => setMapStyle('standard')}
              style={{
                padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', borderRadius: '4px 0 0 4px',
                background: mapStyle === 'standard' ? '#2196F3' : '#fff', color: mapStyle === 'standard' ? '#fff' : '#333',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
              }}
            >Carte</button>
            <button
              onClick={() => setMapStyle('satellite')}
              style={{
                padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', borderRadius: '0 4px 4px 0',
                background: mapStyle === 'satellite' ? '#2196F3' : '#fff', color: mapStyle === 'satellite' ? '#fff' : '#333',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
              }}
            >Satellite</button>
          </div>
          {/* Size legend */}
          <div style={{
            position: 'absolute', bottom: 30, left: 10, zIndex: 1000, background: 'rgba(255,255,255,0.92)',
            padding: '8px 12px', borderRadius: 8, fontSize: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11 }}>Taille = Nb abonnes</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              {[
                { size: 10, label: '< 100' },
                { size: 14, label: '500' },
                { size: 18, label: '1000+' },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: s.size, height: s.size, borderRadius: '50%', background: '#2196F3',
                    border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', margin: '0 auto 3px'
                  }} />
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Side Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
          {/* Tab buttons */}
          <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            {[
              { key: 'legend' as ViewMode, label: 'Legende' },
              { key: 'list' as ViewMode, label: `Liste (${filtered.length})` },
              { key: 'detail' as ViewMode, label: 'Details' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setViewMode(tab.key)}
                style={{
                  flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: viewMode === tab.key ? '#2196F3' : '#fff',
                  color: viewMode === tab.key ? '#fff' : '#666',
                }}
              >{tab.label}</button>
            ))}
          </div>

          {/* Panel content */}
          <div className="chart-card" style={{ flex: 1, overflow: 'auto', padding: 14 }}>
            {viewMode === 'legend' && (
              <>
                <h3 style={{ fontSize: 13, marginBottom: 10 }}>Repartition par partenaire</h3>
                {Object.entries(stats.partenairesCounts)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([name, data]) => (
                  <div key={name} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <div style={{
                        width: 12, height: 12, borderRadius: '50% 50% 50% 0', transform: 'rotate(-45deg)',
                        background: getColor(name), border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', flexShrink: 0
                      }} />
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#333' }}>{name}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: getColor(name) }}>{data.count}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, paddingLeft: 20, fontSize: 11, color: '#888' }}>
                      <span>{data.abonnes.toLocaleString('fr-FR')} abonnes</span>
                      <span>&middot;</span>
                      <span>{Math.round(data.abonnes / (data.count || 1))} moy/forage</span>
                    </div>
                  </div>
                ))}
              </>
            )}

            {viewMode === 'list' && (
              <>
                <h3 style={{ fontSize: 13, marginBottom: 10 }}>Forages ({filtered.length})</h3>
                {filtered.map(f => (
                  <div
                    key={f.id}
                    onClick={() => focusForage(f)}
                    style={{
                      padding: '8px 10px', marginBottom: 6, borderRadius: 6, cursor: 'pointer',
                      border: selectedForage?.id === f.id ? `2px solid ${getColor(f.partenaire)}` : '1px solid #eee',
                      background: selectedForage?.id === f.id ? '#f5f9ff' : '#fff',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%', background: getColor(f.partenaire), flexShrink: 0
                      }} />
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#333', flex: 1 }}>{f.libelle}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#2196F3' }}>{(f.nbAbonnes || 0).toLocaleString('fr-FR')}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#888', paddingLeft: 18, marginTop: 2 }}>
                      {f.partenaire} &middot; {f.localite_nom || 'N/A'}
                    </div>
                  </div>
                ))}
              </>
            )}

            {viewMode === 'detail' && selectedForage && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <h3 style={{ fontSize: 14, color: getColor(selectedForage.partenaire), margin: 0, border: 'none', padding: 0 }}>
                    {selectedForage.libelle}
                  </h3>
                  <button onClick={() => { setSelectedForage(null); setViewMode('list'); }} style={{
                    background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999', padding: 0
                  }}>&times;</button>
                </div>
                <div style={{ fontSize: 11, color: '#999', marginBottom: 12 }}>{selectedForage.reference}</div>
                <div style={{ fontSize: 13 }}>
                  <InfoRow label="Partenaire" value={selectedForage.partenaire} />
                  <InfoRow label="Localite" value={selectedForage.localite_nom} />
                  <InfoRow label="Profondeur" value={selectedForage.profondeur ? selectedForage.profondeur + ' m' : null} />
                  <InfoRow label="Debit" value={selectedForage.debit ? selectedForage.debit + ' m3/h' : null} />
                  <InfoRow label="Annee" value={selectedForage.annee} />
                  <InfoRow label="Abonnes" value={(selectedForage.nbAbonnes || 0).toLocaleString('fr-FR')} highlight />
                  <InfoRow label="Secteurs" value={selectedForage.nbSecteurs} />
                  <InfoRow label="Coordonnees" value={`${parseFloat(selectedForage.lat).toFixed(4)}, ${parseFloat(selectedForage.lng).toFixed(4)}`} />
                </div>

                {/* Abonnes par forage vs moyenne */}
                {stats.totalAbonnes > 0 && (
                  <div style={{ marginTop: 14, padding: '10px 12px', background: '#f5f9ff', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Part des abonnes</div>
                    <div style={{ background: '#e0e0e0', borderRadius: 4, height: 10, overflow: 'hidden' }}>
                      <div style={{
                        width: Math.min(100, ((selectedForage.nbAbonnes || 0) / stats.totalAbonnes) * 100) + '%',
                        height: '100%', borderRadius: 4, background: getColor(selectedForage.partenaire),
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4, textAlign: 'right' }}>
                      {((selectedForage.nbAbonnes || 0) / stats.totalAbonnes * 100).toFixed(1)}% du total
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    if (mapRef.current) mapRef.current.setView([parseFloat(selectedForage.lat), parseFloat(selectedForage.lng)], 14);
                  }}
                  style={{
                    marginTop: 12, width: '100%', padding: '8px 0', background: '#2196F3', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13
                  }}
                >Centrer sur la carte</button>
              </>
            )}

            {viewMode === 'detail' && !selectedForage && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ textAlign: 'center', color: '#aaa' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>&#128205;</div>
                  <div style={{ fontSize: 13 }}>Cliquez sur un marqueur<br/>pour voir les details</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>
    <span style={{ color: '#888' }}>{label}</span>
    <span style={{ fontWeight: highlight ? 700 : 500, color: highlight ? '#2196F3' : '#333' }}>{value || '-'}</span>
  </div>
);

export default Cartographie;
